import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ExotelApiService, TelecallerCallLogsParams, getCallLogsTotal } from '../../../../core/services/exotel/exotel-api.service';
import { LookupDataService } from '../../../../core/services/lookup-data.service';
import { AuthService } from '../../../../core/services/auth/auth.service';
import { CallLogsViewComponent, CallLog, CallStats, FilterOption } from '../../../../shared/components/call-logs-view/call-logs-view.component';

@Component({
  selector: 'app-call-logs',
  standalone: true,
  imports: [CommonModule, CallLogsViewComponent],
  templateUrl: './call-logs.component.html',
  styleUrl: './call-logs.component.css',
})
export class CallLogsComponent implements OnInit {
  private exotelService = inject(ExotelApiService);
  private authService = inject(AuthService);
  private lookupDataService = inject(LookupDataService);

  // Stats
  stats: CallStats = {
    totalCalls: 0,
    totalCallDuration: '0h 0m',
    avgCallDuration: '0m',
    scheduledCalls: 0
  };

  isLoading: boolean = false;

  // Data
  callLogs: CallLog[] = [];
  exotelCallLogs: any[] = [];
  
  // Selected Filters State (Needed for API calls)
  selectedDateRanges: FilterOption[] = [];

  // Server-side pagination state
  currentPage: number = 1;
  pageSize: number = 10;
  totalRecords: number = 0;
  /**
   * The API pages the list, so the table asks us for each page. If the endpoint ever
   * answers with the whole set instead, we hand it over and let the table page locally.
   */
  serverSidePagination: boolean = true;

  ngOnInit(): void {
    this.fetchCallLogs();
  }

  fetchCallLogs(): void {
    const currentUser = this.authService.getCurrentUser();
    const targetTelecallerId = currentUser?.id;

    if (!targetTelecallerId) {
      console.error('No telecaller ID identified');
      return;
    }

    this.isLoading = true;

    // Build API parameters based on selected filters
    const params = this.buildApiParams();

    this.exotelService.getTelecallerCallLogs(targetTelecallerId, params).subscribe({
      next: (response) => {
        this.isLoading = false;

        if (response && response.calls) {
          this.exotelCallLogs = response.calls;
          this.totalRecords = getCallLogsTotal(response);
          this.serverSidePagination = this.totalRecords > response.calls.length;
          this.transformExotelCallLogs();
          this.calculateStats();
        }
      },
      error: (error) => {
        console.error('Error fetching telecaller call logs:', error);
        this.isLoading = false;
      }
    });
  }

  // Handle Date Range Change from Shared Component
  onDateRangeChange(selectedDates: FilterOption[]): void {
    this.selectedDateRanges = selectedDates;
    this.currentPage = 1;
    this.fetchCallLogs();
  }

  // Handle Page Change from Shared Component
  onPageChange(event: { page: number; pageSize: number }): void {
    this.currentPage = event.page;
    this.pageSize = event.pageSize;
    this.fetchCallLogs();
  }

  private buildApiParams(): TelecallerCallLogsParams {
    const params: TelecallerCallLogsParams = {
      page: this.currentPage,
      page_size: this.pageSize
    };

    // Add date range filtering
    if (this.selectedDateRanges.length > 0) {
      const dateRange = this.selectedDateRanges[0]; // Use first selected date range
      const { startTime, endTime } = this.getDateRangeValues(dateRange.id);
      if (startTime) params.start_time = startTime;
      if (endTime) params.end_time = endTime;
    }

    return params;
  }

  private getDateRangeValues(dateRangeId: string): { startTime?: string; endTime?: string } {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (dateRangeId) {
      case 'today':
        return {
          startTime: today.toISOString(),
          endTime: new Date(today.getTime() + 24 * 60 * 60 * 1000).toISOString()
        };
      case 'yesterday':
        const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
        return {
          startTime: yesterday.toISOString(),
          endTime: today.toISOString()
        };
      case 'last7days':
        const last7Days = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        return {
          startTime: last7Days.toISOString(),
          endTime: now.toISOString()
        };
      case 'last30days':
        const last30Days = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        return {
          startTime: last30Days.toISOString(),
          endTime: now.toISOString()
        };
      case 'thisMonth':
        const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return {
          startTime: thisMonthStart.toISOString(),
          endTime: now.toISOString()
        };
      case 'lastMonth':
        const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        return {
          startTime: lastMonthStart.toISOString(),
          endTime: lastMonthEnd.toISOString()
        };
      default:
        return {};
    }
  }

  private transformExotelCallLogs(): void {
    this.callLogs = this.exotelCallLogs.map((call: any) => {
      // Prefer ConversationDuration (actual talk time) from webhook, fall back to Duration
      const durationSec = call.ConversationDuration || call.Duration || 0;
      const minutes = Math.floor(durationSec / 60);
      const seconds = durationSec % 60;
      const formattedDuration = `${minutes}:${seconds.toString().padStart(2, '0')}`;

      // Use patient name from API response, fallback to parsing CustomField
      let patientName = call.patient_name || 'Unknown Patient';
      let telecallerName = call.telecaller_name || this.authService.getCurrentUser()?.name || 'Unknown';

      // If patient name not in response, try parsing CustomField
      if (!call.patient_name && call.CustomField) {
        try {
          // const customData = JSON.parse(call.CustomField); 
          // Parsing might be useful if patient name is hidden there
        } catch (error) {
          // console.warn('Error parsing CustomField:', error);
        }
      }

      // Map Exotel status to our outcome (including webhook-only statuses)
      let outcome: 'COMPLETED' | 'RESCHEDULED' | 'NO_ANSWER' | 'CANCELLED' | 'FAILED' | 'BUSY' = 'COMPLETED';
      if (call.Status) {
        switch (call.Status.toLowerCase()) {
          case 'completed': outcome = 'COMPLETED'; break;
          case 'failed': outcome = 'FAILED'; break;
          case 'no-answer': outcome = 'NO_ANSWER'; break;
          case 'busy': outcome = 'BUSY'; break;
          case 'ringing':
          case 'in-progress': outcome = 'COMPLETED'; break; // treat active calls as ongoing
        }
      }

      const callType = call.Direction === 'inbound' ? 'Incoming' : 'Outgoing';

      // Format dates
      const startDate = new Date(call.StartTime);
      const endDate = new Date(call.EndTime);

      return {
        id: call.Sid,
        callerId: call.From,
        patientName: patientName,
        telecallerName: telecallerName,
        date: startDate.toLocaleDateString('en-GB'),
        startTime: startDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
        endTime: endDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
        duration: formattedDuration,
        nextFollowUp: 'N/A', 
        callType: callType,
        outcome: outcome,
        program: 'General',
        recordingUrl: call.RecordingUrl ? this.exotelService.getProxiedRecordingUrl(call.RecordingUrl) : undefined,
        // Additional fields
        patientId: call.patient_id,
        userId: call.user_id, // Display ID
      };
    });
  }

  private calculateStats(): void {
    const totalDurationSec = this.callLogs.reduce((total, call) => {
      const [minutes, seconds] = call.duration.split(':').map(Number);
      return total + (minutes * 60) + seconds;
    }, 0);

    const avgDurationSec = this.callLogs.length > 0 ? Math.floor(totalDurationSec / this.callLogs.length) : 0;

    this.stats = {
      totalCalls: this.totalRecords,
      totalCallDuration: `${Math.floor(totalDurationSec / 3600)}h ${Math.floor((totalDurationSec % 3600) / 60)}m`,
      avgCallDuration: `${Math.floor(avgDurationSec / 60)}m ${avgDurationSec % 60}s`,
      scheduledCalls: this.callLogs.filter(c => c.outcome === 'RESCHEDULED').length
    };
  }
}
